import { useEffect, useState } from "react";
import BookService from "@app/services/http/book.service";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { Images } from "@assets/images";
import clsx from "clsx";
import BookItem from "@app/components/book-item";
import { Book, BookNote, Meta } from "@app/types";
import BookNoteService from "@app/services/http/book-note.service";
import { RequestParams } from "@core/services/http/http.type";
import { DEFAULT_PAGE, DEFAULT_REQUEST_PARAMS } from "@app/constants";
import Pagination from "@app/components/pagination";

enum LibraryTab {
  HISTORY = 1,
  FAVOURITE = 2,
  PURCHASED = 3,
  NOTED = 4,
}

function History({ books }: { books: Book[] }) {
  return (
    <div>
      {books.length ? (
        <div className="flex gap-8 flex-wrap">
          {books.map((book) => (
            <BookItem key={book.id} book={book} />
          ))}
        </div>
      ) : (
        <div className="flex justify-center mt-32">
          <img src={Images.NoData.default} />
        </div>
      )}
    </div>
  );
}

function Favourite({ books }: { books: Book[] }) {
  return (
    <div>
      {books.length ? (
        <div className="flex gap-8 flex-wrap">
          {books.map((book) => (
            <BookItem key={book.id} book={book} />
          ))}
        </div>
      ) : (
        <div className="flex justify-center mt-32">
          <img src={Images.NoData.default} />
        </div>
      )}
    </div>
  );
}

function Purchased({ books }: { books: Book[] }) {
  return (
    <div>
      {books.length ? (
        <div className="flex gap-8 flex-wrap">
          {books.map((book) => (
            <BookItem key={book.id} book={book} />
          ))}
        </div>
      ) : (
        <div className="flex justify-center mt-32">
          <img src={Images.NoData.default} />
        </div>
      )}
    </div>
  );
}

function Noted() {
  const [bookNotes, setBookNotes] = useState<BookNote[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>({
    ...DEFAULT_REQUEST_PARAMS,
    pageSize: 20,
  });
  const [meta, setMeta] = useState<Meta>();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    BookNoteService.getList(requestParams, abortController.signal).then(
      (res) => {
        setBookNotes(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }

        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    );
  }, []);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      pageNum: page,
    };

    setRequestParams(newRequestParams);
  };

  return (
    <div>
      {bookNotes.length ? (
        <div className="flex gap-8 flex-wrap">
          {bookNotes.map((item, index) => (
            <div
              key={index}
              className="rounded-xl bg-[#FFFBF2] w-[360px] p-2 border-[2px] border-solid border-[#FEECC2]"
            >
              <div className="border-[2px] border-solid border-[#FFEBBC] px-2 rounded-lg bg-[#FFEDB9] font-bold">
                {item.title}
              </div>
              <div className="mt-2 ml-2 border-[2px] border-solid border-[#FFEBBC] px-2 rounded-lg bg-[#FFEBBC]">
                {item.content}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex justify-center mt-32">
          <img src={Images.NoData.default} />
        </div>
      )}
      {meta && (
        <div className="flex justify-center mt-3">
          <Pagination
            currentPage={meta.pageNum}
            totalPages={meta.totalPage}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
}

function LibraryUser() {
  const [activedTab, setActivedTab] = useState(LibraryTab.HISTORY);
  const [booksLibrary, setBooksLibrary] = useState<{
    history: Book[];
    favourite: Book[];
    purchased: Book[];
  }>({
    history: [],
    favourite: [],
    purchased: [],
  });

  const handleTabClick = (tab: any) => {
    setActivedTab(tab);
  };

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    Promise.all([
      BookService.getBookHistory(abortController.signal),
      BookService.getBookFavorite(abortController.signal),
      BookService.getBookPurchased(abortController.signal),
    ]).then((data) => {
      setBooksLibrary({
        history: data[0].data.items,
        favourite: data[1].data.items,
        purchased: data[2].data.items,
      });
    });
  }, []);

  const render = () => {
    switch (activedTab) {
      case LibraryTab.HISTORY:
        return <History books={booksLibrary.history} />;
      case LibraryTab.FAVOURITE:
        return <Favourite books={booksLibrary.favourite} />;
      case LibraryTab.PURCHASED:
        return <Purchased books={booksLibrary.purchased} />;
      case LibraryTab.NOTED:
        return <Noted />;
    }
  };

  return (
    <div>
      <div className="flex gap-10 my-4">
        <div
          onClick={() => handleTabClick(LibraryTab.HISTORY)}
          className={clsx("cursor-pointer font-medium font-[SVN-Gilroy]", {
            "text-[#38499b]": activedTab === LibraryTab.HISTORY,
          })}
        >
          Lịch sử
        </div>
        <div
          onClick={() => handleTabClick(LibraryTab.FAVOURITE)}
          className={clsx("cursor-pointer font-medium font-[SVN-Gilroy]", {
            "text-[#38499b]": activedTab === LibraryTab.FAVOURITE,
          })}
        >
          Yêu thích
        </div>
        <div
          onClick={() => handleTabClick(LibraryTab.PURCHASED)}
          className={clsx("cursor-pointer font-medium font-[SVN-Gilroy]", {
            "text-[#38499b]": activedTab === LibraryTab.PURCHASED,
          })}
        >
          Đã mua
        </div>
        <div
          onClick={() => handleTabClick(LibraryTab.NOTED)}
          className={clsx("cursor-pointer font-medium font-[SVN-Gilroy]", {
            "text-[#38499b]": activedTab === LibraryTab.NOTED,
          })}
        >
          Ghi chú
        </div>
      </div>
      {render()}
    </div>
  );
}

export default LibraryUser;
